import { useState, useEffect } from 'react';

interface SafeImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  fallback?: React.ReactNode;
  retryCount?: number;
  retryInterval?: number;
}

function formatImagePath(src: string): string {
  if (!src) {
    return '';
  }

  // If it's already a URL with protocol, return as is
  if (src.startsWith('http://') || src.startsWith('https://')) {
    return src;
  }

  // If it's a data URL, return as is
  if (src.startsWith('data:')) {
    return src;
  }

  // If it's a protocol-relative URL (starts with //), convert to HTTPS
  if (src.startsWith('//')) {
    return `https:${src}`;
  }

  // For relative paths, ensure they start with /
  if (!src.startsWith('/')) {
    return `/${src}`;
  }

  return src;
}

/**
 * A wrapper around Next.js Image component that:
 * 1. Ensures paths are properly formatted (adds leading / for relative paths)
 * 2. Provides error handling with customizable fallback
 * 3. Handles null/undefined image sources
 * 4. Supports retrying failed image loads
 */
export function SafeImage({
  src,
  alt,
  fallback,
  retryCount = 1,
  retryInterval = 1000,
  ...props
}: SafeImageProps) {
  const [error, setError] = useState(false);
  const [retries, setRetries] = useState(0);
  const [key, setKey] = useState(0);

  useEffect(() => {
    if (error && retries < retryCount) {
      const timer = setTimeout(() => {
        setRetries(retries + 1);
        setError(false);
        setKey((prevKey) => prevKey + 1);
      }, retryInterval);

      return () => clearTimeout(timer);
    }

    return () => {};
  }, [error, retries, retryCount, retryInterval]);

  // If src is null/undefined or we've exhausted all retries and still have an error, show fallback
  if (!src || (error && retries >= retryCount)) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <div className="flex h-full w-full items-center justify-center bg-muted">
        <span className="text-xs text-muted-foreground">{alt || 'Image'}</span>
      </div>
    );
  }

  // Format the source path to ensure it works with Next.js Image
  const formattedSrc = formatImagePath(src);

  return (
    <img key={key} src={formattedSrc} alt={alt || ''} onError={() => setError(true)} {...props} />
  );
}
